import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useERSContext, useMapContext } from '@/context';
import { toastIcons } from '@/lib';
import { useTranslation } from 'react-i18next';
import { useAIProvider } from '@/hooks/useAIProvider';
import { useState, useCallback } from 'react';

export function useToast() {
  const { focusIncident } = useMapContext();
  const { setSelectedChatId, scrollToSelectedChat } = useERSContext();

  const { t, i18n } = useTranslation();
  const [translations, setTranslations] = useState({});

  const { callAPI } = useAIProvider({
    model: 'gpt-4o-mini',
    temperature: 0.3,
  });

  const translateText = useCallback(
    async (text, context = '') => {
      if (!text || typeof text !== 'string' || !text.trim()) {
        return text;
      }

      const cacheKey = `translation-${context}-${text}-${i18n.language}`;

      // Return cached translation if available
      if (translations[cacheKey]) {
        return translations[cacheKey];
      }

      try {
        const prompt = `Translate the following text into ${i18n.language} without adding any extra context or punctuation. Provide only the translation: "${text}"`;

        const translated = await callAPI([{ role: 'user', content: prompt }]);

        if (!translated) {
          return text;
        }

        let cleanedTranslation = translated.trim();

        // Remove surrounding quotes if present
        if (
          (cleanedTranslation.startsWith('"') && cleanedTranslation.endsWith('"')) ||
          (cleanedTranslation.startsWith("'") && cleanedTranslation.endsWith("'"))
        ) {
          cleanedTranslation = cleanedTranslation.slice(1, -1);
        }
        cleanedTranslation = cleanedTranslation.replace(/^["']+|["']+$/g, '').trim();

        // Cache the translation
        setTranslations(prev => ({
          ...prev,
          [cacheKey]: cleanedTranslation,
        }));

        return cleanedTranslation;
      } catch (err) {
        return text;
      }
    },
    [translations, i18n.language, callAPI]
  );

  // Translate toast prefix
  const translateToastPrefix = useCallback(
    async prefix => {
      return translateText(prefix, 'prefix');
    },
    [translateText]
  );

  // Translate description
  const translateDescription = useCallback(
    async description => {
      return translateText(description, 'description');
    },
    [translateText]
  );

  const show = async ({ type, title, description, prefix, createdData, editeddata, profileId }) => {
    // Translate both prefix and description
    let translatedPrefix = prefix;
    let translatedDescription = description;

    if (prefix) {
      translatedPrefix = await translateToastPrefix(prefix);
    }

    if (description) {
      translatedDescription = await translateDescription(description);
    }

    const icon = toastIcons[type] || toastIcons.info;
    const openContent = (cdata, edata, profId) => {
      if (type == null) return;

      if (type === 'incident') {
        focusIncident(cdata || edata);
        return;
      }

      setSelectedChatId(profId);

      // Call scroll after ensuring chat selection
      setTimeout(() => {
        scrollToSelectedChat?.();
      }, 100);
    };

    toast.custom(t => (
      <div
        className="flex justify-between items-center gap-3 p-4 rounded-lg shadow-md bg border w-[460px] min-h-16 hover:cursor-pointer"
        onClick={() => openContent(createdData, editeddata, profileId)}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex items-center text-success-strong flex-shrink-0">{icon}</div>
          <div className="flex flex-col min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground truncate">
              {translatedPrefix ? `${translatedPrefix} ${title}` : title}
            </p>
            {translatedDescription && (
              <p className="text-sm text-muted-foreground truncate" title={translatedDescription}>
                {translatedDescription}
              </p>
            )}
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={() => toast.dismiss(t)}
          aria-label="Close"
          className="text-muted-foreground hover:text-foreground transition flex-shrink-0"
        >
          <X size={16} />
        </button>
      </div>
    ));
  };

  const showToast = async input => {
    if (Array.isArray(input)) {
      for (const item of input) {
        await show(item);
      }
      return;
    }

    if (typeof input === 'string') {
      await show({ title: input });
      return;
    }

    if (typeof input === 'object' && input !== null) {
      await show(input);
    }
  };

  return { showToast };
}
